import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Container,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import Requester from '../../../utils/requester';
import { useNotification } from '../../../context/NotificationContext';

export default function TimerCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Initialize with default values for a new timer
  const [timer, setTimer] = useState({
    name: '',
    message: '',
    interval: 5,
    enabled: true,
    action: 'MessageScheduledAction',
    extra_data: {
      stream_online: true
    }
  });

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTimer(prevTimer => ({
      ...prevTimer,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // Convert to number and ensure it's not negative
    const numValue = Math.max(1, parseInt(value) || 1);
    setTimer(prevTimer => ({
      ...prevTimer,
      [name]: numValue
    }));
  };

  // Handle stream online checkbox
  const handleStreamOnlineChange = (e) => {
    setTimer(prevTimer => ({
      ...prevTimer,
      extra_data: {
        ...prevTimer.extra_data,
        stream_online: e.target.checked
      }
    }));
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    if (!timer.name.trim()) {
      errors.name = [t('timer_form.name_required')];
      isValid = false;
    } else if (timer.name.length > 50) {
      errors.name = [t('timer_form.name_too_long')];
      isValid = false;
    }
    
    if (!timer.message.trim()) {
      errors.message = [t('timer_form.message_required')];
      isValid = false;
    } else if (timer.message.length > 500) {
      errors.message = [t('timer_form.message_too_long')];
      isValid = false;
    }
    
    if (!timer.interval) {
      errors.interval = [t('timer_form.interval_required')];
      isValid = false;
    } else if (timer.interval < 1) {
      errors.interval = [t('timer_form.interval_min')];
      isValid = false;
    }
    
    setFieldErrors(errors);
    return isValid;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await Requester.post('scheduled-actions/create/', timer);
      
      if (response.ok) {
        showNotification(t('timer_form.timer_created'), {
          type: 'success',
          autoHideDuration: 3000
        });
        navigate('/dashboard/timers');
      } else {
        // Try to get error details
        let errorMessage = t('timer_form.error_creating');
        
        try {
          const errorData = await response.json();
          if (typeof errorData === 'object' && errorData !== null) {
            setFieldErrors(errorData);
            
            // Create a message with all error details
            const errorMessages = Object.entries(errorData)
              .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
              .join('\n');
            
            if (errorMessages) {
              errorMessage += ': ' + errorMessages;
            }
          }
        } catch (error) {
          console.error('Error parsing error response:', error);
        }
        
        showNotification(errorMessage, {
          type: 'error',
          autoHideDuration: 6000
        });
      }
    } catch (error) {
      console.error('Error creating timer:', error);
      showNotification(t('timer_form.error_creating'), {
        type: 'error',
        autoHideDuration: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard"
              component={RouterLink}
              sx={{
                '&:hover': { color: 'primary.main' }
              }}
            >
              {t('dashboard_navigation.home')}
            </Link>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard/timers"
              component={RouterLink}
              sx={{
                '&:hover': { color: 'primary.main' }
              }}
            >
              {t('dashboard_navigation.timers')}
            </Link>
            <Typography color="primary">{t('timer_form.creating_timer')}</Typography>
          </Breadcrumbs>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 3 
          }}>
            <TimerIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" fontWeight="500">
              {t('timer_form.creating_timer')}
            </Typography>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 3
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('timer_form.name_tooltip')} arrow>
                    <TextField
                      fullWidth
                      required
                      label={t('timer_form.name_label')}
                      name="name"
                      value={timer.name}
                      onChange={handleChange}
                      margin="normal"
                      error={Boolean(fieldErrors.name)}
                      helperText={fieldErrors.name ? fieldErrors.name[0] : t('timer_form.name_help', { count: timer.name.length })}
                      InputProps={{ 
                        inputProps: { maxLength: 50 } 
                      }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('timer_form.interval_tooltip')} arrow>
                    <TextField
                      fullWidth
                      required
                      label={t('timer_form.interval_label')}
                      name="interval"
                      type="number"
                      value={timer.interval}
                      onChange={handleNumberChange}
                      margin="normal"
                      error={Boolean(fieldErrors.interval)}
                      helperText={fieldErrors.interval ? fieldErrors.interval[0] : null}
                      InputProps={{ 
                        inputProps: { min: 1 } 
                      }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12}>
                  <Tooltip title={t('timer_form.message_tooltip')} arrow>
                    <TextField
                      fullWidth
                      required
                      label={t('timer_form.message_label')}
                      name="message"
                      value={timer.message}
                      onChange={handleChange}
                      margin="normal"
                      multiline
                      rows={4}
                      error={Boolean(fieldErrors.message)}
                      helperText={fieldErrors.message ? fieldErrors.message[0] : t('timer_form.message_help', { count: timer.message.length })}
                      InputProps={{ 
                        inputProps: { maxLength: 500 } 
                      }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('timer_form.enabled_tooltip')} arrow>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={timer.enabled}
                          onChange={handleChange}
                          name="enabled"
                          color="primary"
                        />
                      }
                      label={t('timer_form.enabled_label')}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('timer_form.stream_online_tooltip')} arrow>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={timer.extra_data.stream_online}
                          onChange={handleStreamOnlineChange}
                          name="stream_online"
                          color="primary"
                        />
                      }
                      label={t('timer_form.stream_online_label')}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={saving}
                      sx={{ minWidth: 120 }}
                    >
                      {saving ? <CircularProgress size={24} /> : t('timer_form.create_button')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      component={RouterLink}
                      to="/dashboard/timers"
                      sx={{ minWidth: 120 }}
                    >
                      {t('timer_form.cancel_button')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
} 